import React, { useState, useEffect } from 'react';
import { Badge, Row, Col, Button, Tooltip, OverlayTrigger, Table, Spinner } from 'react-bootstrap';
import { Trash2, Users } from 'lucide-react';
import { API_ENDPOINTS } from '../../../../config/api';
import './VisitorManagement.css';

const calculateAge = (dobString) => {
    if (!dobString) return null;
    const today = new Date();
    const birthDate = new Date(dobString);
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
        age--;
    }
    return age;
};

const VisitorListItem = ({ visitor, onDelete, showGroupBadges = true, onDeleteVisit }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [groupMembers, setGroupMembers] = useState([]);
    const [isLoadingGroup, setIsLoadingGroup] = useState(false);
    const [groupError, setGroupError] = useState("");

    useEffect(() => {
        if (isExpanded && visitor.isGroupVisit && groupMembers.length === 0) {
            fetchGroupMembers();
        }
    }, [isExpanded, visitor.isGroupVisit]);

    const fetchGroupMembers = async () => {
        setIsLoadingGroup(true);
        setGroupError("");
        try {
            const response = await fetch(API_ENDPOINTS.VISITORS.GROUP_MEMBERS(visitor.visitId));
            if (!response.ok) {
                throw new Error("Failed to load group members");
            }
            const data = await response.json();
            setGroupMembers(data.groupMembers || []);
        } catch (err) {
            console.error("Error fetching group members:", err);
            setGroupError("Unable to load group companions.");
        } finally {
            setIsLoadingGroup(false);
        }
    };

    // Generate initials for avatar
    const getInitials = (name) => {
        return name
            .split(' ')
            .map((n) => n[0])
            .join('')
            .toUpperCase()
            .substring(0, 2);
    };

    return (
        <div 
            className="db1-visitor-item p-3 mb-2" 
            onClick={() => setIsExpanded(!isExpanded)}
        >
            <div className="d-flex align-items-center">
                {/* Avatar */}
                <div className="db1-avatar me-3">
                    {getInitials(visitor.fullName)}
                </div>

                {/* Info */}
                <div className="flex-grow-1" style={{ minWidth: 0 }}>
                    <div className="d-flex justify-content-between align-items-center">
                        <div className="d-flex align-items-center flex-wrap gap-2">
                            <h6 className="mb-0 fw-bold" style={{ color: '#2c3e50', fontSize: '0.92rem' }}>{visitor.fullName}</h6>
                            {showGroupBadges && visitor.isGroupVisit && visitor.memberRole === 'PRIMARY' && (
                                <Badge className="db1-badge text-white" style={{ background: 'linear-gradient(135deg, #ac2e62, #c34e7f)' }}>
                                    👥 Group Leader
                                </Badge>
                            )}
                            {showGroupBadges && visitor.isGroupVisit && visitor.memberRole === 'SECONDARY' && (
                                <Badge className="db1-badge text-white" style={{ background: '#7f8c8d' }}>
                                    👥 Group Member
                                </Badge>
                            )}
                            {!!visitor.totalVisits && (
                                <Badge className="db1-badge text-white" style={{ background: '#2bb1ce' }}>
                                    {visitor.totalVisits} {visitor.totalVisits === 1 ? 'visit' : 'visits'}
                                </Badge>
                            )}
                        </div>
                        <div className="text-end" style={{ flexShrink: 0 }}>
                            <div className="fw-bold" style={{ color: '#ac2e62', fontSize: '0.82rem' }}>{visitor.visitTime}</div>
                            <div style={{ color: '#7f8c9b', fontSize: '0.75rem' }}>{visitor.visitDate}</div>
                        </div>
                    </div>
                    <div className="d-flex align-items-center justify-content-between mt-1">
                        <div className="d-flex align-items-center" style={{ gap: '8px' }}>
                            <span style={{ color: '#7f8c9b', fontSize: '0.82rem' }}>{visitor.mobileNumber}</span>
                            <span style={{ color: '#ddd' }}>•</span>
                            <span style={{ color: '#7f8c9b', fontSize: '0.82rem' }}>
                                {visitor.dob ? `${calculateAge(visitor.dob)} yrs` : (visitor.ageGroup || '-')}
                            </span>
                            <span style={{ color: '#ddd' }}>•</span>
                            <span className="db1-badge" style={{ background: 'rgba(172,46,98,0.05)', color: '#ac2e62', padding: '3px 10px', fontSize: '0.72rem' }}>
                                {visitor.purposeOfVisit}
                            </span>
                        </div>
                        <div className="d-flex align-items-center" style={{ gap: '4px' }}>
                            <OverlayTrigger
                                placement="top"
                                overlay={<Tooltip>Delete Visit Record</Tooltip>}
                            >
                                <button 
                                    className="db1-delete-btn"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onDelete(visitor);
                                    }}
                                >
                                    <Trash2 size={15} />
                                </button>
                            </OverlayTrigger>
                            <div 
                                style={{ 
                                    color: '#ac2e62', 
                                    transition: 'transform 0.3s ease', 
                                    transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)', 
                                    fontWeight: 'bold', 
                                    fontSize: '0.9rem',
                                    padding: '0 4px',
                                    opacity: 0.6
                                }}
                            >
                                ➔
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Inline Expansion Area */}
            {isExpanded && (
                <div 
                    className="mt-3 pt-3"
                    onClick={(e) => e.stopPropagation()}
                    style={{ borderTop: '1px solid rgba(172,46,98,0.04)', animation: 'fadeIn 0.25s ease' }}
                >
                    <Row className="g-3">
                        {/* Section 1: Contact Details */}
                        <Col md={6}>
                            <div className="db1-detail-section">
                                <h6 className="fw-bold mb-3" style={{ color: '#ac2e62', fontSize: '0.85rem' }}>📞 Contact Details</h6>
                                <div className="mb-2">
                                    <span style={{ color: '#7f8c9b', fontSize: '0.72rem', display: 'block' }}>Email Address</span>
                                    <span className="fw-semibold" style={{ color: '#2c3e50', fontSize: '0.85rem' }}>{visitor.email || '-'}</span>
                                    <div className="mb-2 mt-2">
                                        <span style={{ color: '#7f8c9b', fontSize: '0.72rem', display: 'block' }}>Date of Birth</span>
                                        <span className="fw-semibold" style={{ color: '#2c3e50', fontSize: '0.85rem' }}>
                                            {visitor.dob
                                                ? new Date(visitor.dob).toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' })
                                                : '-'}
                                        </span>
                                    </div>
                                </div>
                                <div className="mb-2">
                                    <span style={{ color: '#7f8c9b', fontSize: '0.72rem', display: 'block' }}>Address</span>
                                    <span className="fw-semibold" style={{ color: '#2c3e50', fontSize: '0.85rem', whiteSpace: 'pre-line' }}>{visitor.address || '-'}</span>
                                </div>
                                <Row className="g-2 mt-1">
                                    <Col xs={6}>
                                        <span style={{ color: '#7f8c9b', fontSize: '0.72rem', display: 'block' }}>Area / Locality</span>
                                        <span className="fw-semibold" style={{ color: '#2c3e50', fontSize: '0.85rem' }}>{visitor.area || '-'}</span>
                                    </Col>
                                    <Col xs={6}>
                                        <span style={{ color: '#7f8c9b', fontSize: '0.72rem', display: 'block' }}>City</span>
                                        <span className="fw-semibold" style={{ color: '#2c3e50', fontSize: '0.85rem' }}>{visitor.city || '-'}</span>
                                    </Col>
                                    <Col xs={6}>
                                        <span style={{ color: '#7f8c9b', fontSize: '0.72rem', display: 'block' }}>State</span>
                                        <span className="fw-semibold" style={{ color: '#2c3e50', fontSize: '0.85rem' }}>{visitor.state || '-'}</span>
                                    </Col>
                                    <Col xs={6}>
                                        <span style={{ color: '#7f8c9b', fontSize: '0.72rem', display: 'block' }}>Pincode</span>
                                        <span className="fw-semibold" style={{ color: '#2c3e50', fontSize: '0.85rem' }}>{visitor.pincode || '-'}</span>
                                    </Col>
                                </Row>
                            </div>
                        </Col>

                        {/* Section 2: Profile & Demographics */}
                        <Col md={6}>
                            <div className="db1-detail-section">
                                <h6 className="fw-bold mb-3" style={{ color: '#ac2e62', fontSize: '0.85rem' }}>👤 Visitor Profile</h6>
                                <Row className="g-2">
                                    <Col xs={6}>
                                        <span style={{ color: '#7f8c9b', fontSize: '0.72rem', display: 'block' }}>Gender</span>
                                        <span className="fw-semibold" style={{ color: '#2c3e50', fontSize: '0.85rem' }}>{visitor.gender || '-'}</span>
                                    </Col>
                                    <Col xs={6}>
                                        <span style={{ color: '#7f8c9b', fontSize: '0.72rem', display: 'block' }}>Age</span>
                                        <span className="fw-semibold" style={{ color: '#2c3e50', fontSize: '0.85rem' }}>
                                            {visitor.dob ? `${calculateAge(visitor.dob)} years` : (visitor.ageGroup || '-')}
                                        </span>
                                    </Col>
                                    <Col xs={6}>
                                        <span style={{ color: '#7f8c9b', fontSize: '0.72rem', display: 'block' }}>Occupation</span>
                                        <span className="fw-semibold" style={{ color: '#2c3e50', fontSize: '0.85rem' }}>{visitor.occupation || '-'}</span>
                                    </Col>
                                    <Col xs={6}>
                                        <span style={{ color: '#7f8c9b', fontSize: '0.72rem', display: 'block' }}>Organization</span>
                                        <span className="fw-semibold" style={{ color: '#2c3e50', fontSize: '0.85rem' }}>{visitor.organizationName || '-'}</span>
                                    </Col>
                                </Row>
                                <div className="mt-2 d-flex gap-2 flex-wrap">
                                    <div>
                                        <span style={{ color: '#7f8c9b', fontSize: '0.72rem', display: 'block', marginBottom: '2px' }}>Preferred Contact</span>
                                        <span className="db1-badge" style={{ background: 'rgba(172,46,98,0.05)', color: '#ac2e62' }}>
                                            {visitor.preferredContactMethod || 'WhatsApp'}
                                        </span>
                                    </div>
                                    <div>
                                        <span style={{ color: '#7f8c9b', fontSize: '0.72rem', display: 'block', marginBottom: '2px' }}>NGO Updates</span>
                                        <span className="db1-badge text-white" style={{ background: visitor.receiveUpdates === 'Yes' ? '#28a745' : '#7f8c8d' }}>
                                            {visitor.receiveUpdates || 'Yes'}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </Col>

                        {/* Section 3: Visit Notes */}
                        <Col xs={12}>
                            <div className="db1-detail-section">
                                <h6 className="fw-bold mb-2" style={{ color: '#ac2e62', fontSize: '0.85rem' }}>📝 Latest Visit Notes & Remarks</h6>
                                <p className="mb-2 fw-medium" style={{ fontStyle: 'italic', fontSize: '0.85rem', color: '#555' }}>
                                    {visitor.notes ? `"${visitor.notes}"` : 'No notes provided for this visit.'}
                                </p>
                                {visitor.interestedCauses && visitor.interestedCauses.length > 0 && (
                                    <div className="mt-2 pt-2" style={{ borderTop: '1px solid rgba(172,46,98,0.04)' }}>
                                        <span style={{ color: '#7f8c9b', fontSize: '0.72rem', display: 'block', marginBottom: '4px' }}>Supported Causes</span>
                                        <div className="d-flex flex-wrap gap-1">
                                            {visitor.interestedCauses.map(cause => (
                                                <span key={cause} className="db1-badge" style={{ background: 'rgba(172,46,98,0.04)', color: '#ac2e62', padding: '3px 10px' }}>
                                                    {cause}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </Col>
                        {/* Section: Complete Visit History */}
                        {visitor.visitHistory && visitor.visitHistory.length > 0 && (
                            <Col xs={12}>
                                <div className="p-3 rounded border" style={{ fontSize: '0.9rem', backgroundColor: '#fcfcfc' }}>
                                    <h6 className="fw-bold mb-3" style={{ color: '#ac2e62', fontSize: '0.95rem' }}>
                                        🕒 Complete Visit History ({visitor.visitHistory.length} visits)
                                    </h6>
                                    <div className="table-responsive">
                                        <Table hover size="sm" className="mb-0 align-middle" style={{ backgroundColor: 'white', borderRadius: '8px', overflow: 'hidden', border: '1px solid #eee' }}>
                                            <thead style={{ backgroundColor: '#f5f5f5' }}>
                                                <tr>
                                                    <th style={{ fontSize: '0.75rem', fontWeight: '700', textTransform: 'uppercase', color: '#666', padding: '10px' }}>#</th>
                                                    <th style={{ fontSize: '0.75rem', fontWeight: '700', textTransform: 'uppercase', color: '#666', padding: '10px' }}>Date</th>
                                                    <th style={{ fontSize: '0.75rem', fontWeight: '700', textTransform: 'uppercase', color: '#666', padding: '10px' }}>Time</th>
                                                    <th style={{ fontSize: '0.75rem', fontWeight: '700', textTransform: 'uppercase', color: '#666', padding: '10px' }}>Branch</th>
                                                    <th style={{ fontSize: '0.75rem', fontWeight: '700', textTransform: 'uppercase', color: '#666', padding: '10px' }}>Purpose</th>
                                                    <th style={{ fontSize: '0.75rem', fontWeight: '700', textTransform: 'uppercase', color: '#666', padding: '10px' }}>Type</th>
                                                    <th style={{ fontSize: '0.75rem', fontWeight: '700', textTransform: 'uppercase', color: '#666', padding: '10px' }}>Action</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {visitor.visitHistory.map((v, idx) => (
                                                    <tr key={v.visitId || idx}>
                                                        <td style={{ padding: '10px', color: '#888', fontSize: '0.8rem' }}>{idx + 1}</td>
                                                        <td style={{ padding: '10px', fontWeight: '600', color: '#2c3e50' }}>{v.date}</td>
                                                        <td style={{ padding: '10px', color: '#ac2e62', fontWeight: '600' }}>{v.time}</td>
                                                        <td style={{ padding: '10px', color: '#555', fontSize: '0.82rem' }}>
                                                            {v.branch === 'Maainagari' && '🏠 Maainagari'}
                                                            {v.branch === 'Shree Manashanti Chhatralaya' && '🏫 Shree Manashanti Chhatralaya'}
                                                            {v.branch === 'TMGF Central Office' && '🏢 TMGF Central Office'}
                                                            {v.branch === 'Maainagari - Solapur' && '📍 Maainagari - Solapur'}
                                                            {v.branch === 'Maainagari - Goa' && '📍 Maainagari - Goa'}
                                                            {!v.branch && <span className="text-muted">—</span>}
                                                        </td>
                                                        <td style={{ padding: '10px', color: '#555' }}>{v.purpose}</td>
                                                        <td style={{ padding: '10px' }}>
                                                            <Badge bg={v.isGroupVisit ? 'warning' : 'info'} text="dark" style={{ fontSize: '0.7rem' }}>
                                                                {v.isGroupVisit ? '👥 Group' : '👤 Solo'}
                                                            </Badge>
                                                        </td>
                                                        <td style={{ padding: '10px' }}>
                                                            {onDeleteVisit && (
                                                                <button
                                                                    className="db1-delete-btn"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        onDeleteVisit(v.visitId, v.date, visitor.fullName);
                                                                    }}
                                                                    title="Delete this visit"
                                                                >
                                                                    <Trash2 size={14} />
                                                                </button>
                                                            )}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>
                                    </div>
                                </div>
                            </Col>
                        )}
                        {/* Section 4: Group Members Table */}
                        {visitor.isGroupVisit && (
                            <Col xs={12}>
                                <div className="db1-detail-section">
                                    <h6 className="fw-bold mb-3 d-flex align-items-center" style={{ color: '#ac2e62', fontSize: '0.85rem' }}>
                                        <Users size={15} className="me-2" /> Group Check-in Companions
                                    </h6>
                                    {isLoadingGroup ? (
                                        <div className="d-flex align-items-center justify-content-center py-3">
                                            <div style={{ width: '18px', height: '18px', borderRadius: '50%', border: '2px solid rgba(172,46,98,0.08)', borderTopColor: '#ac2e62', animation: 'spin 0.6s linear infinite', marginRight: '8px' }} />
                                            <span style={{ color: '#7f8c9b', fontSize: '0.82rem' }}>Loading group members...</span>
                                        </div>
                                    ) : groupError ? (
                                        <span className="text-danger small">{groupError}</span>
                                    ) : groupMembers.length > 0 ? (
                                        <div className="table-responsive">
                                            <Table hover size="sm" className="mb-0 text-start align-middle" style={{ backgroundColor: 'white', borderRadius: '8px', overflow: 'hidden', border: '1px solid rgba(0,0,0,0.03)' }}>
                                                <thead style={{ backgroundColor: 'rgba(172,46,98,0.03)' }}>
                                                    <tr>
                                                        <th style={{ fontSize: '0.72rem', fontWeight: '700', textTransform: 'uppercase', color: '#7f8c9b', padding: '10px', borderBottom: '1px solid rgba(172,46,98,0.06)' }}>Name</th>
                                                        <th style={{ fontSize: '0.72rem', fontWeight: '700', textTransform: 'uppercase', color: '#7f8c9b', padding: '10px', borderBottom: '1px solid rgba(172,46,98,0.06)' }}>Mobile Number</th>
                                                        <th style={{ fontSize: '0.72rem', fontWeight: '700', textTransform: 'uppercase', color: '#7f8c9b', padding: '10px', borderBottom: '1px solid rgba(172,46,98,0.06)' }}>Role</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {groupMembers.map((member, mIdx) => {
                                                        const isLeader = member.memberRole === 'PRIMARY';
                                                        const mVisitor = member.visitor || {};
                                                        return (
                                                            <tr key={member.groupMemberId || mIdx} style={{ backgroundColor: isLeader ? 'rgba(172, 46, 98, 0.02)' : 'transparent' }}>
                                                                <td style={{ padding: '10px', fontWeight: isLeader ? '700' : '500', color: isLeader ? '#ac2e62' : '#2c3e50', borderBottom: '1px solid rgba(0,0,0,0.02)' }}>
                                                                    {isLeader ? '👑 ' : '👤 '}
                                                                    {mVisitor.fullName || 'Unknown Visitor'}
                                                                </td>
                                                                <td style={{ padding: '10px', color: '#7f8c9b', borderBottom: '1px solid rgba(0,0,0,0.02)' }}>
                                                                    {mVisitor.mobileNumber || '-'}
                                                                </td>
                                                                <td style={{ padding: '10px', borderBottom: '1px solid rgba(0,0,0,0.02)' }}>
                                                                    <Badge 
                                                                        style={{ 
                                                                            backgroundColor: isLeader ? '#ac2e62' : '#7f8c8d',
                                                                            border: 'none',
                                                                            fontSize: '0.68rem',
                                                                            padding: '3px 8px',
                                                                            borderRadius: '999px',
                                                                            fontWeight: 600
                                                                        }}
                                                                    >
                                                                        {isLeader ? 'Primary (Leader)' : 'Companion'}
                                                                    </Badge>
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </Table>
                                        </div>
                                    ) : (
                                        <span style={{ color: '#7f8c9b', fontSize: '0.82rem' }}>No group members found.</span>
                                    )}
                                </div>
                            </Col>
                        )}
                    </Row>
                </div>
            )}
        </div>
    );
};

export default VisitorListItem;
